import { ThemeOption } from './../../../models/_core/theme-option';
import { NavigationService } from 'src/app/services/navigation/navigation.service';
import { UserState } from './../../../models/_core/user-state';
import { HelperUtilitiesService } from 'src/app/services/_core/helper-utilities/helper-utilities.service';
import { AlertController, ModalController } from '@ionic/angular';
import { environment } from '../../../../environments/environment';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject, firstValueFrom } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { NotificationsService } from '../notifications/notifications.service';
import { StorageService } from '../storage/storage.service';
import moment from 'moment';
import { AuthState } from 'src/app/models/_core/auth-state';
import { SSOTokenResponse } from 'src/app/models/_core/sso-token-response';
import { UserDeviceService } from '../user-device/user-device.service';
import { Browser } from '@capacitor/browser';
import { AwsSettings } from 'src/app/models/_core/aws-settings';
import { AnalyticsService } from '../analytics/analytics.service';
import { User } from 'src/app/models/user';
import { AuthLegacyService } from '../auth-legacy/auth-legacy.service';
import { AuthSsoService } from '../auth-sso/auth-sso.service';
import { Device } from '@capacitor/device';

/**
 * ID: bh-auth-service
 * Name: BH Auth Service
 * Description: Service used for managing authentication and user state
 * Version: 5
 *
 * ==============================
 * Change Log
 * ==============================
 * 2021-07-02 - MW - v1: Initial dev
 * 2021-07-13 - MW - v2: Implemented userState
 * 2021-07-27 - MW - v3: Improved open modal + alert handling; improved UX
 * 2022-05-23 - MW - v4: Updated depreciated value/error handling
 * 2022-05-27 - MW - v5: Implemented user state and theme subjects
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  env = environment;
  authUser: BehaviorSubject<User> = new BehaviorSubject(null);
  userState: UserState = {};
  userStateSubject: BehaviorSubject<UserState> = new BehaviorSubject({});
  themeSubject: BehaviorSubject<ThemeOption> = new BehaviorSubject('light');
  menuOpen: BehaviorSubject<boolean> = new BehaviorSubject(false);
  apiUrl: any;
  timeoutWarningMs = 60000;
  timeoutLogoutMs = 120000;
  inactivitySubject = new BehaviorSubject<number>(0);
  inactivityTimer = null;
  private timeoutWarningShown = false;
  private timeoutLogoutTriggered = false;
  targetUrl = '';
  awsCognitoClientId = this.env.awsCognitoClientId;
  surveyInactivitySubject = new BehaviorSubject<number>(0);
  surveyInactivityTimer = null;
  surveyTimeoutLogoutMs = 120000;
  surveyTimeoutWarningMs = 60000;
  constructor(
    private http: HttpClient,
    private notifications: NotificationsService,
    private storageService: StorageService,
    private alertCtrl: AlertController,
    private modalCtrl: ModalController,
    private helpers: HelperUtilitiesService,
    private navService: NavigationService,
    private deviceService: UserDeviceService,
    private analytics: AnalyticsService,
    private authLegacy: AuthLegacyService,
    private authSso: AuthSsoService
  ) {
    this.initializeUserState();
  }

  private async initializeUserState() {
    this.userState = await this.storageService.getData('userState');
  }

  async loadAwsSettings(): Promise<any> {
    try {
      const settings = await firstValueFrom(this.getAwsSettings());
      console.log('loadAwsSettings', settings);
      Promise.resolve(settings);
    } catch (err) {
      console.error('loadAwsSettings: error: ', err);
      throw err;
    }
  }

  /**
   * Gets Auth User object
   * Recommend subscribing to authUser directly
   */
  getAuthUser(): User {
    const authUser = this.authUser.getValue();
    return authUser ? authUser : {} as User;
  }

  /***
   * Updates Auth User object with provided object
   * @param authUser User object to replace existing value
   */
  setAuthUser(authUser: User) {
    this.authUser.next(authUser);
  }

  /**
   * Gets User State object
   * Recommend subscribing to userStateSubject directly
   */
  getUserState(): UserState {
    return this.userStateSubject.getValue();
  }

  /***
   * Updates User State subject object
   * @param userState User State to update with
   */
  setUserState(userState: UserState) {
    this.userStateSubject.next(userState);
  }

  /**
   * Toggles the menu open and closed
   */
  toggleMenu() {
    const isMenuOpen = this.menuOpen.getValue();
    this.menuOpen.next(!isMenuOpen);
  }

  /**
   * Gets active theme
   * Recommend subscribing to themeSubject directly
   */
  getTheme(): ThemeOption {
    return this.themeSubject.getValue();
  }

  /***
   * Updates theme subject object
   * @param theme ThemeOption to update with
   */
  setTheme(theme: ThemeOption) {
    this.themeSubject.next(theme);
  }



  /***
   * Save the user's state to local storage
   */
  async saveUserStateToStorage() {
    console.log('saveUserStateToStorage: Starting to save userState:', this.userState);
    if (!this.env.storeToken && this.userState.authUser && this.userState.authUser.token) {
      this.userState.authUser.token = null;
      console.log('saveUserStateToStorage: Removed token from userState for non-token storage');
    }
    this.userStateSubject.next(this.userState);
    console.log('saveUserStateToStorage: Calling storageService.saveData with userState:', this.userState);
    const saveResult = await this.storageService.saveData('userState', this.userState);
    console.log('saveUserStateToStorage: Storage save result:', saveResult);
  }

  /**
   * Starts inactivity timer.
   * Should be called after successfully logging in or when entering non-DeviceRouteGuard paths.
   * Idempotent: does nothing if the timer is already running.
   * @param forceOnActivityRoutes If true, start the timer even when requireTimeout is false (used on activity-timer routes so timeout notification still shows).
   */
  public startInactivityTimer(forceOnActivityRoutes = false) {
    if (this.inactivityTimer) {
      return;
    }
    const shouldStart = this.env.requireTimeout || forceOnActivityRoutes;
    if (shouldStart && this.env.timeoutThreshold) {
      this.timeoutLogoutMs = this.env.timeoutThreshold;
      this.timeoutWarningMs = this.timeoutLogoutMs - 30000;
      this.inactivitySubject.next(0);
      this.inactivityTimer = setInterval(() => {
        let time = this.inactivitySubject.getValue();
        time += 1000;
        // console.log('Inactivity: ', time)
        this.inactivitySubject.next(time);
        this.checkForTimeout();
      }, 1000);
    }
  }

  public startInactivityTimerForSurvey() {
    if (this.env.requireTimeoutForSurvey) {
      this.surveyTimeoutLogoutMs = this.env.surveyTimeoutThreshold; // 120000 milliseconds = 2 minutes
      //this.surveyTimeoutWarningMs = this.surveyTimeoutLogoutMs - 30000;
      this.surveyInactivityTimer = setInterval(() => {
        let time = this.surveyInactivitySubject.getValue();
        time += 1000;
        // console.log('Inactivity: ', time)
        this.surveyInactivitySubject.next(time);
        this.checkForSurveyTimeout();
      }, 1000);
    }
  }

  public checkForSurveyTimeout() {
    const time = this.surveyInactivitySubject.getValue();
    if (time >= this.surveyTimeoutLogoutMs) {
      this.logout(true, true);
    }
  }

  /**
   * Check for session timeout, display appropriate alert if timing out.
   */
  public async checkForTimeout() {
    const time = this.inactivitySubject.getValue();
    if (time >= this.timeoutWarningMs && time < this.timeoutLogoutMs && !this.timeoutWarningShown) {
      this.timeoutWarningShown = true;
      const alert = await this.alertCtrl.create({
        header: 'Still there?',
        message: 'You will be signed out soon due to inactivity.',
        cssClass: 'wide-alert warning',
        backdropDismiss: false,
        buttons: [
          {
            text: 'Stay signed in',
            cssClass: 'primary',
            handler: (val) => {
              this.bumpInactivityTimer();
            }
          },
          {
            text: 'Sign out',
            handler: async (val) => {
              await this.dismissAllModalsAndAlerts();
              this.logout(false, true);
            }
          }
        ]
      });
      await alert.present();
    } else if (time >= this.timeoutLogoutMs && !this.timeoutLogoutTriggered) {
      this.timeoutLogoutTriggered = true;
      await this.dismissAllModalsAndAlerts();
      this.logout(true, true);
    }
  }

  /**
   * Dismisses all open alerts and modals
   */
  async dismissAllModalsAndAlerts(): Promise<boolean> {
    // Dismiss alerts
    for (let i = 0; i < 25; i++) {
      const alert = await this.alertCtrl.getTop();
      if (alert) {
        await alert.dismiss();
      } else {
        break;
      }
    }

    // Dismiss modals
    for (let i = 0; i < 25; i++) {
      const modal = await this.modalCtrl.getTop();
      if (modal) {
        await modal.dismiss();
      } else {
        break;
      }
    }

    return Promise.resolve(true);

  }

  /**
   * Bumps activity timer, preventing auto-timeout
   */
  public bumpInactivityTimer() {
    this.timeoutWarningShown = false;
    this.timeoutLogoutTriggered = false;
    this.inactivitySubject.next(0);
  }

  /**
   * Bumps inactivity timer only if it is currently running (e.g. started by guard on activity routes).
   * Used by HTTP interceptor so API calls reset the timer when the timer is active.
   */
  public bumpInactivityTimerIfRunning() {
    if (this.inactivityTimer) {
      this.bumpInactivityTimer();
    }
  }

  /**
   * Bumps survey activity timer, preventing auto-timeout
   */
  public bumpSurveyInactivityTimer() {
    this.surveyInactivitySubject.next(0);
  }

  /**
   * Stops the survey inactivity timer
   */
  public stopSurveyInactivityTimer() {
    this.surveyInactivitySubject.next(0);
    if (this.surveyInactivityTimer) {
      clearInterval(this.surveyInactivityTimer);
      this.surveyInactivityTimer = null;
    }
  }

  validateUser(): Observable<any> {
    if (this.env.ssoEnabled) {
      return this.authSso.validateUser();
    } else {
      return this.authLegacy.validateUser();
    }
  }

  getAwsSettings(): Observable<AwsSettings> {
    const url = `${this.env.apiUrl}/settings`;
    return this.http.get(url).pipe(
      map((data: any) => {
        console.log('getAwsSettings', data);
        return data;
      }),
      // catchError(error => of(error))
    );
  }

  async authorizeRedirect() {
    // const settings = await firstValueFrom(this.getAwsSettings());
    // console.log('*** authorizeRedirect: settings: ', settings);
    const redirectRoot = this.getUriFromLocation();
    const redirectUri = redirectRoot + '/redirect';
    const url = `${this.env.awsCognitoUrl}/oauth2/authorize?` +
      `response_type=code&client_id=${this.awsCognitoClientId}&scope=openid%20profile&` +
      `redirect_uri=${redirectUri}`;

  this.notifications.showAlert('URL', redirectRoot);
    console.log('*** authorizeRedirect: ', url);
    console.log('authorizeRedirect', url);
    location.href = url;
  }

  async openAuthBrowser(url): Promise<void> {
    await Browser.addListener('browserPageLoaded', () => {
      // Do something when browser page is loaded
      console.log('browser page finished loading', Browser);
    });
    // await Browser.addListener('urlChangeEvent', () => {

    // });
    await Browser.open({
      windowName: 'auth',
      url,
      presentationStyle: 'popover'
    });
    return Promise.resolve();
  }

  getSSOToken(authCode: string): Observable<SSOTokenResponse> {
    const redirectRoot = this.getUriFromLocation();
    const redirectUri = redirectRoot + '/redirect';
    const url = `${this.env.awsCognitoUrl}/oauth2/token?client_id=${this.awsCognitoClientId}`;
    console.log('getSSOToken: url', url);
    const body = {
      grant_type: 'authorization_code',
      code: authCode,
      redirect_uri: redirectUri
    };
    const encodedBody = this.convertToFormData(body);
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
      'BH-DONT-ASSIGN-HEADERS': 'true'
    });
    return this.http.post(url, encodedBody, { headers }).pipe(
      map((data: any) => {
        return data;
      }),
      // catchError(error => of(error))
    );
  }

  refreshSSOToken(refreshToken: string): Observable<SSOTokenResponse> {
    const url = `${this.env.awsCognitoUrl}/oauth2/token?client_id=${this.awsCognitoClientId}`;
    const body = {
      refresh_token: refreshToken,
      grant_type: 'refresh_token'
    };
    const encodedBody = this.convertToFormData(body);
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
      'BH-DONT-ASSIGN-HEADERS': 'true',
      'BH-TOKEN-REFRESH': 'true'
    });
    return this.http.post(url, encodedBody, { headers }).pipe(
      map((data: SSOTokenResponse) => {
        // Update user token
        const authUser = this.getAuthUser();
        authUser.token = data.access_token;
        this.setAuthUser(authUser);

        // Update stored token
        const userState = this.getUserState();
        userState.authUser = authUser;
        this.setUserState(userState);

        // console.log('refreshed token: ', authUser, userState, data);
        return data;
      }),
      // catchError(error => of(error))
    );
  }

  private convertToFormData(data: any): string {
    const formData = new URLSearchParams();

    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        formData.set(key, data[key]);
      }
    }

    return formData.toString();
  }

  /***
   * Logs user into application
   * @param userName User Name
   * @param password  Password
   * @returns User Login Payload
   */
  login(userId, password): Observable<any> {
    const url = `${this.env.apiUrl}/users/login`;
    const body = {
      userId,
      password,
    };
    return this.http.post(url, body, { observe: 'response'}).pipe(
      switchMap(async (data: any) => {
        await this.handleLoginResponse(data.body);
        return data.body;
      }),
    );
  }

  /***
   * Process user response data, determining login status
   * @param data Login Response Data
   */
  async handleLoginResponse(data: any) {
    console.log('handleLoginResponse: data', data);
    if (data) {
      //this.startInactivityTimer();
      const authUser: User = data;
      const userId = (authUser.id || authUser.userId);
      authUser.userId = userId.toLowerCase();
      authUser.firstName = this.helpers.getFirstName(data.name);
      authUser.lastName = this.helpers.getLastName(data.name);
      authUser.role = this.setRole(authUser);
      authUser.token = data.jwtToken;

      // Load device information and set it on authUser
      try {
        const deviceInfo = await this.deviceService.loadDeviceProperties();
        authUser.deviceInfo = deviceInfo;
        console.log('Device info loaded and set on authUser:', deviceInfo);
      } catch (error) {
        console.error('Error loading device information:', error);
        // Set empty device info if loading fails
        authUser.deviceInfo = {};
      }

      this.setAuthUser(authUser);
      console.log('handleLoginResponse: setAuthUser called with:', authUser);

      // Initialize userState if it doesn't exist
      if (!this.userState) {
        this.userState = {};
        console.log('handleLoginResponse: Initialized new userState');
      }

      this.userState.sessionAppVersion = this.env.appVersion + '-' + this.env.env;
      this.userState.userId = authUser.userId;
      this.userState.userName = authUser.userName;
      this.userState.environment = this.env;
      this.userState.lastLoggedIn = moment().format('M/D/YYYY HH:mm');
      this.userState.authState = AuthState.LOGGED_IN;

      if (this.env.storeToken) {
        this.userState.authUser = authUser;
        console.log('handleLoginResponse: Added authUser to userState for storage');
      }

      console.log('handleLoginResponse: userState before saving:', this.userState);

      // Always save userState to storage
      await this.saveUserStateToStorage();
      console.log('handleLoginResponse: userState saved to storage');
    }
    return;
  }

  async getDeviceInfo() {
    const device = this.deviceService.getUserDevice();
    console.log('Device info', device);
    const deviceId = await Device.getId();
    const deviceInfo = await Device.getInfo();
    console.log('Device ID', deviceId);
    console.log('Device Info', deviceInfo);
  }

  async initSession(ssoTokenRes: SSOTokenResponse): Promise<void> {
    try {
      let authUser = this.getAuthUser();
      authUser.token = ssoTokenRes.access_token;
      this.setAuthUser(authUser);
      const res = await firstValueFrom(this.validateUser());
      if (res && res.id) {
        // Update user object
        authUser = res;
        authUser.userId = authUser.id;
        authUser.userName = authUser.userName;
        authUser.firstName = this.helpers.getFirstName(res.fullName);
        authUser.role = this.setRole(authUser);
        // Update analytics user
        this.analytics.analyticsData.userid = authUser.userName.toLowerCase();
        // Reapply the access token
        authUser.token = ssoTokenRes.access_token;
        // Set user
        this.setAuthUser(authUser);
        // Load navigation
        // this.navService.loadNavigation();
        // Initialize userState if it doesn't exist
        if (!this.userState) {
          this.userState = {};
        }

        // Prepare App State
        this.userState.sessionAppVersion = this.env.appVersion + '-' + this.env.env;
        this.userState.userId = authUser.userId;
        this.userState.userName = authUser.userName;
        this.userState.environment = this.env;
        this.userState.lastLoggedIn = moment().format('M/D/YYYY HH:mm');
        this.userState.authState = AuthState.LOGGED_IN;
        this.userState.authUser = authUser;
        await this.saveUserStateToStorage();
        // Save refresh token
        await this.storageService.saveData('refreshToken', ssoTokenRes.refresh_token, true);
        const rtok = this.storageService.getData('refreshToken', true);
        // console.log('initSession: rtok: ', rtok);
      }
      return Promise.resolve();
    } catch (err) {
      throw err;
    }
  }

  setRole(authUser: User): 'USER' | 'ADMIN' | 'SYS_ADMIN' | 'admin' | 'sys_admin' | 'user' {
    if (authUser.roles) {
      if (authUser.roles.includes('SYS_ADMIN')) {
        return 'sys_admin';
      } else if (authUser.roles.includes('ADMIN')) {
        return 'admin';
      } else {
        return 'user';
      }
    } else if (authUser.role) {
      return authUser.role;
    } else {
      return 'user';
    }
  }

  /**
   * Reconstructs userState from /me endpoint response
   * @param meResponse Response from /me endpoint
   * @param existingToken Existing token if available
   */
  async reconstructUserStateFromMe(meResponse: any, existingToken?: string): Promise<UserState> {
    const authUser: User = {
      userId: meResponse.id,
      userName: meResponse.userName,
      firstName: this.helpers.getFirstName(meResponse.fullName || meResponse.name),
      lastName: this.helpers.getLastName(meResponse.fullName || meResponse.name),
      role: this.setRole(meResponse),
      token: existingToken || null,
      authenticated: false
    };

    const userState: UserState = {
      sessionAppVersion: this.env.appVersion + '-' + this.env.env,
      userId: authUser.userId,
      userName: authUser.userName,
      environment: this.env,
      lastLoggedIn: moment().format('M/D/YYYY HH:mm'),
      authState: AuthState.LOGGED_IN,
      authUser: authUser
    };

    return userState;
  }

  decodeJwt(token: string): any {
    if (token) {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));

      return JSON.parse(jsonPayload);
    } else {
      return null;
    }
  }

  isTokenExpired(token: string): boolean {
    const decodedToken = this.decodeJwt(token);
    // console.log('authService: isTokenExpired: ', decodedToken);

    if (decodedToken && decodedToken.exp) {
      const expirationTime = decodedToken.exp * 1000; // Convert to milliseconds
      const currentTime = new Date().getTime();
      // const expireMoment = moment(expirationTime);
      // console.log('authService: expireMoment: ', expireMoment.format('M/D/YYYY h:mm a'));

      return expirationTime <= currentTime;
    }

    // If the 'exp' claim is not present, consider the token as not expired
    return false;
  }

  getUriFromLocation(): string {
    // Get the current location
    const currentLocation = window.location;

    // Extract protocol, domain, and port
    const protocol = currentLocation.protocol;
    const domain = currentLocation.hostname;
    const port = currentLocation.port;
    const portSuffix = (port && port !== '80') ? ':' + port : '';

    console.log("Protocol:", protocol);
    console.log("Domain:", domain);
    console.log("Port:", port);

    if (protocol === 'capacitor:' || this.deviceService.isNotBrowser()) {
      // Return web url for universal links
      return this.env.webUrl;
    } else {
      // Return localhost
      return protocol + '//' + domain + portSuffix;
    }

    // return protocol + '//' + domain + portSuffix;

  }

  /***
   * Logs user out
   * @param isExpired Determines if session expired
   * @param redirectToLogin Designates redirection to login page
   */
  logout(isExpired = false, redirectToLogin = true) {
    this.authUser.next(null);
    this.timeoutWarningShown = false;
    this.timeoutLogoutTriggered = false;
    this.inactivitySubject.next(0);
    this.surveyInactivitySubject.next(0);
    clearInterval(this.inactivityTimer);
    clearInterval(this.surveyInactivityTimer);
    this.inactivityTimer = null;
    this.surveyInactivityTimer = null;
    this.navService.navPages = [];
    this.alertCtrl.getTop().then(alert => {
      if (alert) {
        alert.dismiss();
      }
    });
    if (isExpired) {
      this.userState.authState = AuthState.EXPIRED;
      this.notifications.showAlert('Session expired', 'You were signed out due to inactivity.', 'danger');
    } else {
      this.userState.authState = AuthState.LOGGED_OUT;
    }

    this.storageService.removeData('userState');

    if (redirectToLogin) {
      this.navService.navigateBack('login');
    }
  }
}
